/**
 * Ship status (Ready to Ship / Made to Order) line item property.
 *
 * 1. On add-to-cart, writes the correct "Ship" label into the hidden
 *    properties[Ship] input for the variant that is CURRENTLY selected,
 *    right before the theme's own product-form submit handler (js/common/product/product-form.js,
 *    "onSubmit_fn") reads the form via FormData and posts to /cart/add.js.
 * 2. On cart drawer/page load, silently corrects any line whose stored
 *    Ship value no longer matches the variant's live status (e.g. inventory
 *    sold out after the item was added), via /cart/change.js, then asks the
 *    theme to redraw via the existing "cart:refresh" event.
 *
 * Label classification itself lives in snippets/shipping-status.liquid — this
 * file only reads the values that snippet already rendered (the inline JSON
 * map on the product form, or the ?view=ship-status endpoint for live checks).
 */
(() => {
  function readShipStatusMap(form) {
    const script = form.querySelector('[data-ship-status-map]');
    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return null;
    }
  }

  /**
   * Capture phase (3rd argument "true") so this runs before product-form's own
   * bubble-phase "submit" listener builds the FormData that gets posted to
   * /cart/add.js. We only ever mutate the hidden input's value here — the
   * existing add-to-cart / cart:change / variant:add flow is untouched.
   */
  document.addEventListener('submit', (event) => {
    const form = event.target;

    if (!form.matches || !form.matches('form[action*="/cart/add"]')) {
      return;
    }

    const shipInput = form.querySelector('[data-ship-property-input]');
    if (!shipInput) return;

    const map = readShipStatusMap(form);
    if (!map) return; // no map rendered for this form: the server-rendered fallback value stands

    const selectedVariantId = form.elements.id && form.elements.id.value;
    const label = selectedVariantId && map[selectedVariantId];

    if (label) {
      shipInput.value = label;
    }
  }, true);

  /**
   * Stale cart correction: compares each line's stored properties.Ship against
   * the variant's current live status (fetched via the product's
   * ?view=ship-status alternate template, see templates/product.ship-status.liquid,
   * which renders the same snippets/shipping-status.liquid map used at add-to-cart
   * time) and silently fixes any mismatch via /cart/change.js, preserving quantity
   * and all other properties.
   */
  async function fetchShipStatusMap(handle) {
    try {
      const response = await fetch(`${Shopify.routes.root}products/${handle}?view=ship-status`);
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  let reconciling = false;

  async function reconcileCartShipStatus() {
    if (reconciling) return;
    reconciling = true;

    try {
      const cartResponse = await fetch(`${Shopify.routes.root}cart.js`);
      if (!cartResponse.ok) return;
      const cart = await cartResponse.json();
      if (!cart.items || cart.items.length === 0) return;

      const handles = [...new Set(cart.items.map((item) => item.handle))];
      const mapEntries = await Promise.all(
        handles.map(async (handle) => [handle, await fetchShipStatusMap(handle)])
      );
      const mapsByHandle = Object.fromEntries(mapEntries);

      let correctedAnyLine = false;

      for (const item of cart.items) {
        const storedShipValue = item.properties && item.properties['Ship'];
        if (!storedShipValue) continue; // line was never Ship-tracked, leave it alone

        const map = mapsByHandle[item.handle];
        const liveShipValue = map && map[String(item.variant_id)];
        if (!liveShipValue || liveShipValue === storedShipValue) continue;

        const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: item.key,
            quantity: item.quantity,
            properties: { ...item.properties, Ship: liveShipValue }
          })
        });

        if (response.ok) {
          correctedAnyLine = true;
        }
      }

      if (correctedAnyLine) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    } finally {
      reconciling = false;
    }
  }

  document.addEventListener('DOMContentLoaded', reconcileCartShipStatus);
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) reconcileCartShipStatus();
  });
  // Re-check after real cart mutations (add/quantity change) dispatched by the theme.
  // Not listening to "cart:refresh" itself, since we dispatch that ourselves above.
  document.addEventListener('cart:change', reconcileCartShipStatus);
})();
